import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Globe, 
  Palette, 
  MessageSquare, 
  Calendar, 
  Bell,
  Save,
  Link,
  Upload,
  ExternalLink,
  Smartphone,
  Mail,
  MapPin,
  Phone,
  Loader2,
  Copy
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import './PortalSettings.css';

const PortalSettings = () => {
  const { addNotification } = useNotifications();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Portal Config States
  const [portalEnabled, setPortalEnabled] = useState(true);
  const [portalBookingEnabled, setPortalBookingEnabled] = useState(true);
  const [portalShowSpecialty, setPortalShowSpecialty] = useState(true);
  const [portalShowInsurance, setPortalShowInsurance] = useState(true);
  const [portalMinTimeBefore, setPortalMinTimeBefore] = useState('2');
  const [portalUrl, setPortalUrl] = useState('https://gdt.com/portal/consultorio-gdt');
  const [portalWelcomeMessage, setPortalWelcomeMessage] = useState('');
  const [portalAddress, setPortalAddress] = useState('');
  const [portalEmail, setPortalEmail] = useState('');
  const [portalPhone, setPortalPhone] = useState('');
  const [portalWhatsapp, setPortalWhatsapp] = useState('');
  const [portalPrimaryColor, setPortalPrimaryColor] = useState('#00e5ff');
  const [portalCancelLimit, setPortalCancelLimit] = useState('24');
  const [portalInstructions, setPortalInstructions] = useState('');
  const [portalLogo, setPortalLogo] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.from('system_settings').select('*');
      if (error) throw error;
      
      if (data) {
        const getVal = (key, fallback) => {
          const row = data.find(r => r.key === key);
          return row ? row.value : fallback;
        };
        
        setPortalEnabled(getVal('portal_enabled', 'true') === 'true');
        setPortalBookingEnabled(getVal('portal_booking_enabled', 'true') === 'true');
        setPortalShowSpecialty(getVal('portal_show_specialty', 'true') === 'true');
        setPortalShowInsurance(getVal('portal_show_insurance', 'true') === 'true');
        setPortalMinTimeBefore(getVal('portal_min_time_before', '2'));
        setPortalUrl(getVal('portal_url', 'https://gdt.com/portal/consultorio-gdt'));
        setPortalWelcomeMessage(getVal('portal_welcome_message', ''));
        setPortalAddress(getVal('portal_address', ''));
        setPortalEmail(getVal('portal_email', ''));
        setPortalPhone(getVal('portal_phone', ''));
        setPortalWhatsapp(getVal('portal_whatsapp', ''));
        setPortalPrimaryColor(getVal('portal_primary_color', '#00e5ff'));
        setPortalCancelLimit(getVal('portal_cancel_limit', '24'));
        setPortalInstructions(getVal('portal_instructions', ''));
        setPortalLogo(getVal('portal_logo', ''));
      }
    } catch (err) {
      console.error('Error loading portal settings:', err);
      addNotification('Error al cargar la configuración', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const settingsToSave = [
        { key: 'portal_enabled', value: portalEnabled.toString() },
        { key: 'portal_booking_enabled', value: portalBookingEnabled.toString() },
        { key: 'portal_show_specialty', value: portalShowSpecialty.toString() },
        { key: 'portal_show_insurance', value: portalShowInsurance.toString() },
        { key: 'portal_min_time_before', value: portalMinTimeBefore },
        { key: 'portal_url', value: portalUrl },
        { key: 'portal_welcome_message', value: portalWelcomeMessage },
        { key: 'portal_address', value: portalAddress },
        { key: 'portal_email', value: portalEmail },
        { key: 'portal_phone', value: portalPhone },
        { key: 'portal_whatsapp', value: portalWhatsapp },
        { key: 'portal_primary_color', value: portalPrimaryColor },
        { key: 'portal_cancel_limit', value: portalCancelLimit },
        { key: 'portal_instructions', value: portalInstructions },
        { key: 'portal_logo', value: portalLogo }
      ];

      const { error } = await supabase.from('system_settings').upsert(settingsToSave, { onConflict: 'key' });
      if (error) throw error;
      
      addNotification('Configuración del portal guardada', 'success');
    } catch (err) {
      console.error('Error saving portal settings:', err);
      addNotification('Error al guardar configuración', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePortalLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPortalLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(portalUrl);
    addNotification('Enlace del portal copiado al portapapeles', 'success');
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Globe },
    { id: 'booking', label: 'Turnos Online', icon: Calendar },
    { id: 'content', label: 'Contenido', icon: MessageSquare },
    { id: 'appearance', label: 'Apariencia', icon: Palette },
    { id: 'notifications', label: 'Notificaciones', icon: Bell },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="tab-pane animate-fade-in">
            <div className="form-group">
              <label>Link del Portal para Pacientes</label>
              <div className="input-with-action">
                <div className="input-icon-wrapper">
                  <Link size={18} />
                  <input 
                    type="text" 
                    value={portalUrl} 
                    onChange={(e) => setPortalUrl(e.target.value)} 
                    placeholder="Ej: https://gdt.com/portal/gdt"
                  />
                </div>
                <button className="btn-secondary flex-center gap-2" onClick={handleCopyLink} type="button">
                  COPIAR <Copy size={14} />
                </button>
              </div>
              <p className="form-hint">Este es el link que debes compartir con tus pacientes para que puedan reservar turnos.</p>
            </div>

            <div className="form-group">
              <div className="toggle-container">
                <div className="toggle-info">
                  <span className="toggle-label">¿ Portal Habilitado ?</span>
                  <p className="toggle-hint">Determina si los pacientes pueden acceder al portal web.</p>
                </div>
                <label className="switch">
                  <input 
                    type="checkbox" 
                    checked={portalEnabled} 
                    onChange={(e) => setPortalEnabled(e.target.checked)} 
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>
        );
      case 'booking':
        return (
          <div className="tab-pane animate-fade-in">
            <div className="form-group">
              <div className="toggle-container">
                <div className="toggle-info">
                  <span className="toggle-label">¿ Habilitada la toma de Turnos ?</span>
                  <p className="toggle-hint">Habilita o deshabilita la posibilidad de agendar nuevas citas online.</p>
                </div>
                <label className="switch">
                  <input 
                    type="checkbox" 
                    checked={portalBookingEnabled} 
                    onChange={(e) => setPortalBookingEnabled(e.target.checked)} 
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <div className="toggle-container">
                <div className="toggle-info">
                  <span className="toggle-label">¿ Mostrar Filtro por Especialidad ?</span>
                  <p className="toggle-hint">Permite al paciente agrupar/filtrar las prácticas médicas por especialidad.</p>
                </div>
                <label className="switch">
                  <input 
                    type="checkbox" 
                    checked={portalShowSpecialty} 
                    onChange={(e) => setPortalShowSpecialty(e.target.checked)} 
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <div className="toggle-container">
                <div className="toggle-info">
                  <span className="toggle-label">¿ Mostrar Filtro por Obra Social / Seguro ?</span>
                  <p className="toggle-hint">Pide datos de obra social al registrar al paciente. Si se desactiva, se registra como Particular.</p>
                </div>
                <label className="switch">
                  <input 
                    type="checkbox" 
                    checked={portalShowInsurance} 
                    onChange={(e) => setPortalShowInsurance(e.target.checked)} 
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Hasta cuánto tiempo antes se puede tomar un turno</label>
                <select value={portalMinTimeBefore} onChange={(e) => setPortalMinTimeBefore(e.target.value)}>
                  <option value="1">1 hora antes</option>
                  <option value="2">2 horas antes</option>
                  <option value="4">4 horas antes</option>
                  <option value="12">12 horas antes</option>
                  <option value="24">24 horas antes</option>
                  <option value="48">48 horas antes</option>
                </select>
                <p className="form-hint">Márgen mínimo de tiempo respecto del turno para permitir la reserva online.</p>
              </div>
              <div className="form-group">
                <label>Límite de Cancelación Online</label>
                <select value={portalCancelLimit} onChange={(e) => setPortalCancelLimit(e.target.value)}>
                  <option value="12">12 horas antes</option>
                  <option value="24">24 horas antes</option>
                  <option value="48">48 horas antes</option>
                </select>
                <p className="form-hint">Tiempo mínimo para cancelar o reprogramar un turno online.</p>
              </div>
            </div>
          </div>
        );
      case 'content':
        return (
          <div className="tab-pane animate-fade-in">
            <div className="form-group">
              <label>Mensaje de Bienvenida</label>
              <textarea 
                rows="3" 
                placeholder="Ej: Bienvenido a nuestro sistema de autogestión de turnos..."
                value={portalWelcomeMessage}
                onChange={(e) => setPortalWelcomeMessage(e.target.value)}
              ></textarea>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Dirección física del Consultorio</label>
                <div className="input-icon-wrapper">
                  <MapPin size={18} />
                  <input 
                    type="text" 
                    value={portalAddress} 
                    onChange={(e) => setPortalAddress(e.target.value)} 
                    placeholder="Ej: Av. Libertador 1234, CABA"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Email de Contacto</label>
                <div className="input-icon-wrapper">
                  <Mail size={18} />
                  <input 
                    type="email" 
                    value={portalEmail} 
                    onChange={(e) => setPortalEmail(e.target.value)} 
                    placeholder="Ej: contacto@gdt.com"
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Teléfono</label>
                <div className="input-icon-wrapper">
                  <Phone size={18} />
                  <input 
                    type="text" 
                    value={portalPhone} 
                    onChange={(e) => setPortalPhone(e.target.value)} 
                    placeholder="Ej: 011 4444-5555"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>WhatsApp Oficial</label>
                <div className="input-icon-wrapper">
                  <Smartphone size={18} />
                  <input 
                    type="text" 
                    value={portalWhatsapp} 
                    onChange={(e) => setPortalWhatsapp(e.target.value)} 
                    placeholder="Ej: 5491112345678"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      case 'appearance':
        return (
          <div className="tab-pane animate-fade-in">
            <div className="appearance-grid">
              <div className="upload-section">
                <label>Logo del Consultorio</label>
                <div className="logo-preview-box" style={{ position: 'relative', overflow: 'hidden' }}>
                  <img src={portalLogo || "https://ui-avatars.com/api/?name=GDT&background=00e5ff&color=000&size=128"} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div className="upload-overlay" style={{ zIndex: 2 }}>
                    <Upload size={24} />
                    <span>CAMBIAR</span>
                  </div>
                  <input 
                    type="file" 
                    accept="image/png, image/jpeg" 
                    onChange={handlePortalLogoUpload}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 3 }}
                  />
                </div>
              </div>

              <div className="color-section">
                <label>Color Temático Primario</label>
                <div className="color-picker-container">
                  <input 
                    type="color" 
                    value={portalPrimaryColor} 
                    onChange={(e) => setPortalPrimaryColor(e.target.value)} 
                  />
                  <span className="color-code">{portalPrimaryColor.toUpperCase()}</span>
                </div>
                <p className="form-hint">Este color se aplicará a botones primarios, enlaces e indicadores en el portal.</p>
              </div>
            </div>
          </div>
        );
      case 'notifications':
        return (
          <div className="tab-pane animate-fade-in">
            <div className="notification-channels">
              <h3>Canales de Notificación para Reservas</h3>
              <div className="channel-item">
                <div className="channel-info">
                  <Mail size={20} />
                  <span>Enviar Recordatorio por Email</span>
                </div>
                <label className="switch">
                  <input type="checkbox" defaultChecked />
                  <span className="slider round"></span>
                </label>
              </div>
              <div className="channel-item">
                <div className="channel-info">
                  <Smartphone size={20} />
                  <span>Enviar Recordatorio por WhatsApp</span>
                </div>
                <label className="switch">
                  <input type="checkbox" defaultChecked />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            <div className="form-group margin-top-2">
              <label>Instrucciones Adicionales (Se imprimen al pie del comprobante)</label>
              <textarea 
                rows="4" 
                placeholder="Instrucciones que se verán al pie del mensaje..."
                value={portalInstructions}
                onChange={(e) => setPortalInstructions(e.target.value)}
              ></textarea>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="portal-settings-page flex-center" style={{ justifyContent: 'center', height: '60vh' }}>
        <div className="flex-center gap-2 text-muted">
          <Loader2 size={24} className="animate-spin text-cyan" />
          <span>Cargando configuración del portal...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-settings-page">
      <div className="portal-settings-header">
        <div className="title-section">
          <Settings size={32} className="icon-header" />
          <div>
            <h1>Configuración de Portal</h1>
            <p>Personaliza la experiencia de tus pacientes al reservar online.</p>
          </div>
        </div>
        <button className="btn-save-portal" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 size={18} className="animate-spin" /> GUARDANDO...
            </>
          ) : (
            <>
              <Save size={18} /> GUARDAR CAMBIOS
            </>
          )}
        </button>
      </div>

      <div className="settings-container card">
        <div className="settings-sidebar">
          {tabs.map(tab => (
            <button 
              key={tab.id}
              className={`sidebar-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              type="button"
            >
              <tab.icon size={20} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
        
        <div className="settings-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default PortalSettings;
